import { FC, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Accordion,
  AccordionItem,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { SidePanel } from '@carbon/ibm-products';
import { SidePanelResizeHandle } from 'components/resizable-side-panel';
import { EMPTY_CELL } from 'utils/empty-cell';
import { formatConfidence } from 'utils/number';
import { useExclusiveDrawer } from './exclusive-drawer-hooks';
import { FIRST_SUGGESTION_LABEL, suggestionLabels } from './utils';
import type { AgentDetails, PartyAutoMatch, SuggestionSummary } from './types';
import 'components/inline-edit';
import style from './agent-details-panel.module.scss';

interface Props {
  open: boolean;
  onClose: () => void;
  details: AgentDetails | null;
  autoMatch?: PartyAutoMatch | null;
  suggestions: SuggestionSummary[];
  partyName: string | null;
  partyId: number | null;
}

export const AgentDetailsPanel: FC<Props> = ({
  open,
  onClose,
  details,
  autoMatch,
  suggestions,
  partyName,
  partyId,
}) => {
  const pageContentSelector = useExclusiveDrawer(
    'agent-details',
    open,
    onClose
  );

  // Every persisted candidate in the server's ranking, labelled as the tiles
  // label them. The "no match" sentinel has no entity, so no label.
  const candidates = useMemo(() => {
    const labels = suggestionLabels(suggestions);
    return suggestions.flatMap((suggestion) => {
      const label = labels.get(suggestion.id);
      if (!label) return [];
      const recommended =
        label === FIRST_SUGGESTION_LABEL && suggestion.isAiRecommendation;
      return [
        { suggestion, label: recommended ? `${label} (Recommended)` : label },
      ];
    });
  }, [suggestions]);

  const summary = details?.summaryMarkdown?.trim() || null;

  const autoMatchNote =
    autoMatch?.outcome === 'manual_review' ? autoMatch.suppressionNote : null;

  const subtitle = partyName ? (
    <span>
      Match evaluation for{' '}
      {partyId ? (
        <Link href={`/entities/${partyId}`}>{partyName}</Link>
      ) : (
        partyName
      )}
    </span>
  ) : (
    'How this party was matched.'
  );

  return (
    <>
      <SidePanelResizeHandle
        open={open}
        panelSelector=".agent-details-sidepanel"
        pageContentSelector={pageContentSelector}
        defaultWidthPx={640}
      />
      <SidePanel
        open={open}
        onRequestClose={onClose}
        title="Match Details"
        subtitle={subtitle}
        className={`agent-details-sidepanel ${style.sidePanel}`}
        slideIn
        selectorPageContent={pageContentSelector}
        placement="right"
        size="lg"
      >
        <div className={style.body}>
          {autoMatchNote && (
            <section className={style.section}>
              <p className={style.summaryHeading}>Auto-Match</p>
              <div className={style.markdown}>
                <p>{autoMatchNote}</p>
              </div>
            </section>
          )}
          {details === null ? (
            <p className={style.empty}>
              No completed match run for this party yet. Re-run Match to
              generate a match analysis.
            </p>
          ) : (
            <>
              <section className={style.section}>
                <p className={style.summaryHeading}>Summary</p>
                <div className={style.markdown}>
                  {summary ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {summary}
                    </ReactMarkdown>
                  ) : (
                    <p>{EMPTY_CELL}</p>
                  )}
                </div>
              </section>

              {details.model && (
                <section className={style.section}>
                  <p className={style.summaryHeading}>Model</p>
                  <div className={style.markdown}>
                    <p>{details.model}</p>
                  </div>
                </section>
              )}

              {candidates.length > 0 && (
                <Accordion>
                  {candidates.map(({ suggestion, label }, idx) => {
                    const scorePct = formatConfidence(
                      suggestion.confidenceScore
                    );
                    const title = `${label} — Confidence Breakdown — ${scorePct}`;
                    const factors = suggestion.confidenceFactors ?? [];
                    const reviewReason = suggestion.reviewReason?.trim();

                    return (
                      <AccordionItem
                        key={suggestion.id}
                        title={title}
                        open={idx === 0}
                      >
                        {factors.length === 0 ? (
                          <p className={style.empty}>
                            No per-factor breakdown was recorded for this
                            candidate.
                          </p>
                        ) : (
                          <Table size="sm" className={style.factorTable}>
                            <TableHead>
                              <TableRow>
                                <TableHeader>Factor</TableHeader>
                                <TableHeader>Score</TableHeader>
                                <TableHeader>Reasoning</TableHeader>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {factors.map((f) => (
                                <TableRow key={f.factor}>
                                  <TableCell className={style.factorCell}>
                                    {f.factor}
                                  </TableCell>
                                  <TableCell className={style.scoreCell}>
                                    {formatConfidence(f.scored)}
                                  </TableCell>
                                  <TableCell className={style.reasoningCell}>
                                    {f.note?.trim() || '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                        {reviewReason && (
                          <div className={style.agentNote}>
                            <span className={style.agentNoteLabel}>
                              Agent Note
                            </span>
                            <p>{reviewReason}</p>
                          </div>
                        )}
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </>
          )}
        </div>
      </SidePanel>
    </>
  );
};
